import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import * as _ from 'lodash';
import moment from 'moment';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { Subject, filter, distinctUntilChanged, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  CUSTOM_FIELD_TYPE_MULTI_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
  CUSTOM_FIELD_TYPE_SINGLE_SELECT,
  CUSTOM_FIELD_TYPE_USER_SELECT,
  CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
  MAX_FILE_SIZE_MB,
} from 'src/app/constants';
import { init as initEvaluateConditions, getVisibleFields } from 'src/app/utils/custom-fields';
import { CustomField, CustomFieldMetadata } from 'src/models';
import { AIAutofillService } from 'src/services/ai-autofill.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { UploadService } from 'src/services/upload.service';

import { ActionDropdownType, ActionDropdownComponent } from '../action-dropdown/action-dropdown.component';
import { DatetimeModalComponent } from '../datetime-modal/datetime-modal.component';
import { MiniFileAttachmentPreviewComponent } from '../mini-file-attachment-preview/mini-file-attachment-preview.component';

export type CustomFieldValue = string | string[] | boolean | null | undefined;
export type CustomFieldValues = Record<string, CustomFieldValue>;

/**
 * TicketTypeFieldsComponent
 *
 * This component displays and manages ticket type fields.
 * It emits changes only when not disabled and when changes are initiated by user interaction.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxTippyModule, MiniFileAttachmentPreviewComponent],
  selector: 'app-ticket-type-fields',
  templateUrl: './ticket-type-fields.component.html',
  styleUrls: ['./ticket-type-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketTypeFieldsComponent implements OnInit, OnChanges, OnDestroy {
  // Make public so template can access it
  readonly componentInstanceId = uuidv4();

  @Input() fieldDefinitions: CustomField[] = [];
  @Input() fieldValues: CustomFieldValues = {};
  @Input() fieldMetadata: CustomFieldMetadata[] = [];
  @Input() disabled = false;
  @Output() fieldValuesChange = new EventEmitter<CustomFieldValues>();
  @Input() conversationId: string | undefined;
  @Input() customerId: string | undefined;
  @Input() projectId: string | null = null;
  @Input() ticketTypeId: string | undefined;

  ticketTypeFieldLabels: Record<string, Record<string, string>> = {};
  uploadingFieldId: string | null = null;
  protected readonly Array = Array;

  // Helper to get the field value as a string, for display with labels or pipes expecting strings.
  // Returns an empty string if the value is not a string or is null/undefined.
  getFieldValueAsString(fieldId: string): string {
    const value = this.fieldValues[fieldId];
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      // Convert array of strings to a comma-separated string
      // Filter out non-string items just in case, though fieldValues should ideally be clean
      return value.filter((item) => typeof item === 'string').join(', ');
    }
    return '';
  }

  // Helper to get the field value specifically for date fields.
  // Returns Date, string, or number if appropriate, otherwise null for the date pipe.
  getFieldValueForDate(fieldId: string): string | number | Date | null {
    const value = this.fieldValues[fieldId];
    if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
      return value;
    }
    return null;
  }

  // Helper to get the field value as an array of strings, for *ngFor on multi-select chips, etc.
  getFieldValueAsArray(fieldId: string): string[] {
    const value = this.fieldValues[fieldId];
    if (Array.isArray(value)) {
      return value.filter((item) => typeof item === 'string');
    }
    if (typeof value === 'string') {
      return [value];
    }
    return [];
  }

  // RxJS Subject for field updates
  private fieldUpdates = new Subject<{
    fieldId: string;
    value: any;
    source: 'user' | 'external';
  }>();

  // Flag to track update source
  private updateSource: 'user' | 'external' = 'user';

  // Last emitted fields object to prevent duplicate emissions
  private lastEmittedFields: any = null;

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private uploadService: UploadService,
    private authService: AuthService,
    private popoverCtrl: PopoverController,
    private aiAutofillService: AIAutofillService,
  ) {
    // Setup the subscription to only process updates from user interaction
    this.fieldUpdates
      .pipe(
        filter((update) => !this.disabled && update.source === 'user'),
        distinctUntilChanged((prev, curr) => {
          // Use lodash's isEqual for more robust deep comparison
          return prev.fieldId === curr.fieldId && _.isEqual(prev.value, curr.value);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((update) => {
        const newFields = {
          ...this.fieldValues,
          [update.fieldId]: update.value,
        };

        // Only emit if value actually changed using lodash deep equality
        if (!this.lastEmittedFields || !_.isEqual(this.lastEmittedFields, newFields)) {
          this.lastEmittedFields = _.cloneDeep(newFields);

          // Emit the change to parent within NgZone
          this.ngZone.run(() => {
            this.fieldValuesChange.emit(newFields);
          });
        }
      });
  }

  // Helper function to find metadata for a specific custom field
  private getFieldMetadata(fieldId: string): CustomFieldMetadata | undefined {
    if (!this.fieldMetadata || !Array.isArray(this.fieldMetadata)) {
      return undefined;
    }
    return this.fieldMetadata.find((metadata) => metadata.customFieldId === fieldId);
  }

  // Helper method for template to check if field has unapproved prefill
  shouldShowPrefillButton(fieldId: string): boolean {
    const metadata = this.getFieldMetadata(fieldId);
    return (
      metadata?.isPreFilled === true && (metadata.approvedPrefill === undefined || metadata.approvedPrefill === null)
    );
  }

  async ngOnInit() {
    await initEvaluateConditions();

    // Initial field setup is now simpler as parent controls what's rendered.
    // We still need to fetch labels for the initial set of fields.
    this.updateSource = 'external';
    await this.getTicketFieldFieldLabels();
    this.cdr.detectChanges();
    this.updateSource = 'user';
  }

  async ngOnChanges(changes: SimpleChanges) {
    let needsLabelUpdate = false;

    if (changes['fieldDefinitions']) {
      // If the set of fields to display changes, we need to update their labels.
      needsLabelUpdate = true;
    }

    if (changes['fieldValues']) {
      // If values change externally, we might need to update labels if they are value-dependent,
      // and ensure UI reflects the new values (though ngModel should handle most of this).
      // For now, let's assume if values change, labels depending on those values might need refresh.
      // This also covers the initial binding of fieldValues.
      this.updateSource = 'external'; // Mark as external update for the initial value processing
      // If fieldValues (values) change, it might affect which labels are needed if some labels show current values.
      needsLabelUpdate = true;
      // No need to call getTicketTypeFieldsFlat anymore.
      // We also don't call handleUpdate here, as this is an external change to values.
      this.cdr.detectChanges(); // Ensure UI updates with new values
      this.updateSource = 'user'; // Reset
    }

    if (needsLabelUpdate) {
      this.updateSource = 'external';
      await this.getTicketFieldFieldLabels();
      this.cdr.detectChanges();
      this.updateSource = 'user';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getTicketFieldFieldLabels() {
    const allRenderedFields = this.fieldDefinitions;

    if (!allRenderedFields || allRenderedFields.length === 0) {
      this.ticketTypeFieldLabels = {};
      return;
    }

    const userSelectFields = allRenderedFields
      .filter(
        (f): f is CustomField & { type: string } =>
          typeof (f as any)?.type === 'string' &&
          [CUSTOM_FIELD_TYPE_USER_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_SELECT].includes((f as any).type),
      )
      .flatMap((f: CustomField & { type: string }) => {
        const result = this.fieldValues?.[f.id];
        return Array.isArray(result) ? result : [result];
      })
      .filter((v: any) => v);

    const userGroupSelectFields = allRenderedFields
      .filter(
        (f): f is CustomField & { type: string } =>
          typeof (f as any)?.type === 'string' &&
          [CUSTOM_FIELD_TYPE_USER_GROUP_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT].includes((f as any).type),
      )
      .flatMap((f: CustomField & { type: string }) => {
        const result = this.fieldValues?.[f.id];
        return Array.isArray(result) ? result : [result];
      })
      .filter((v: any) => v);
    const externalSelectFields = allRenderedFields
      .filter(
        (
          f,
        ): f is CustomField & {
          type: string;
          options: any[];
          externalSelect: string;
        } =>
          typeof (f as any)?.type === 'string' &&
          [CUSTOM_FIELD_TYPE_SINGLE_SELECT, CUSTOM_FIELD_TYPE_MULTI_SELECT].includes((f as any).type) &&
          (f as any).options == null &&
          typeof (f as any).externalSelect === 'string',
      )
      .flatMap(
        (
          f: CustomField & {
            type: string;
            options: any[];
            externalSelect: string;
          },
        ) => {
          const result = this.fieldValues?.[f.id];
          const resultArray = Array.isArray(result) ? result : [result];
          return resultArray.map((v: any) => `${f.externalSelect}:${v}`);
        },
      );

    if (userSelectFields.length || externalSelectFields.length || userGroupSelectFields.length) {
      const lookupValues: any = await this.apiService.postPromise('/saved-filters/get-lookup-values', {
        assigneeId: userSelectFields,
        externalSelectValue: externalSelectFields,
        userGroupId: userGroupSelectFields,
      });

      this.ticketTypeFieldLabels[CUSTOM_FIELD_TYPE_USER_SELECT] = lookupValues.assigneeId;
      this.ticketTypeFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] = lookupValues.externalSelectValue;
      this.ticketTypeFieldLabels[CUSTOM_FIELD_TYPE_USER_GROUP_SELECT] = lookupValues.userGroupId;
    }
  }

  /**
   * All interactive methods below will just check if component is disabled
   * before doing anything
   */

  async showTicketTypeFieldOptions(field: CustomField) {
    if (this.disabled) {
      return;
    }
    // Ensure field has a 'type' property before using it
    if (typeof field.type !== 'string') {
      console.warn('Field is missing type property in showTicketTypeFieldOptions', field);
      return;
    }
    const fieldType = field.type;

    const isMultiSelect = [
      CUSTOM_FIELD_TYPE_MULTI_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
    ].includes(fieldType);
    const isUserSelect = [CUSTOM_FIELD_TYPE_USER_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_SELECT].includes(fieldType);

    const isUserGroupSelect = [CUSTOM_FIELD_TYPE_USER_GROUP_SELECT, CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT].includes(
      fieldType,
    );

    const onSaveCallback = (id: string, label: string) => {
      let valueToSet: CustomFieldValue = null;
      const currentFieldValue = this.fieldValues?.[field.id];

      if (isMultiSelect) {
        const currentArray = Array.isArray(currentFieldValue) ? currentFieldValue : [];
        // Ensure currentArray elements are strings before spreading with string `id`
        const currentStringArray = currentArray.filter((item) => typeof item === 'string') as string[];
        valueToSet = Array.from(new Set<string>([...currentStringArray, id]));
      } else {
        if (id !== currentFieldValue) {
          // Direct comparison is fine for single-select primitive values
          valueToSet = id;
        } else {
          return; // No change
        }
      }

      this.handleUpdate(field.id, valueToSet);

      if (
        'externalSelect' in field &&
        typeof field.externalSelect === 'string' &&
        field.externalSelect != null &&
        typeof valueToSet === 'string'
      ) {
        this.ticketTypeFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] = {
          ...(this.ticketTypeFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT] ?? {}),
          [`${field.externalSelect}:${valueToSet}`]: label,
        };
      }
    };

    if (isUserSelect) {
      const currentVal = this.fieldValues?.[field.id];
      const selectedId = typeof currentVal === 'string' ? currentVal : null;
      this.searchablePopoverService.users({
        event,
        selectedUserId: selectedId,
        callback: onSaveCallback,
        where: field.allowedValues ? [{ field: 'id', operator: 'in', value: field.allowedValues }] : undefined,
      });
    } else if (isUserGroupSelect) {
      const currentVal = this.fieldValues?.[field.id];
      const selectedId = typeof currentVal === 'string' ? currentVal : null;
      this.searchablePopoverService.userGroups({
        event,
        selectedGroupId: selectedId,
        callback: onSaveCallback,
        where: field.allowedValues ? [{ field: 'id', operator: 'in', value: field.allowedValues }] : undefined,
      });
    } else {
      this.searchablePopoverService.customFieldOptions({
        event,
        field,
        callback: onSaveCallback,
        showNoneOption: true,
        context: {
          conversationId: this.conversationId,
          userId: this.authService.userId,
          customerId: this.customerId,
        },
        contextOverrides: {
          conversation: {
            ticketTypeId: this.ticketTypeId,
            ticketTypeFields: this.fieldValues,
          },
        },
      });
    }
  }

  async changeDateTicketTypeField(field: CustomField) {
    if (this.disabled) {
      return;
    }

    // Determine the initial selected date for the modal.
    // If this.fieldValues[field.id] is already 'YYYY-MM-DD', new Date() will parse it correctly.
    // If it's a Date object or full ISO string, that's also fine for new Date().
    const currentFieldValue = this.fieldValues[field.id];
    const initialDate = typeof currentFieldValue === 'string' ? moment(currentFieldValue) : currentFieldValue;

    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        title: 'Select date',
        selected: initialDate,
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        // Convert the result to 'YYYY-MM-DD' string before storing
        const formattedDate = moment(result).format('YYYY-MM-DD');
        this.handleUpdate(field.id, formattedDate);
      }
    });
  }

  removeTicketTypeField(field: CustomField, valueToRemove: string) {
    if (this.disabled) {
      return;
    }
    const currentFieldValue = this.fieldValues[field.id];
    if (Array.isArray(currentFieldValue)) {
      // Ensure all items in currentFieldValue are strings before filtering
      const currentStringArray = currentFieldValue.filter((item) => typeof item === 'string') as string[];
      this.handleUpdate(
        field.id,
        currentStringArray.filter((v: string) => v !== valueToRemove),
      );
    }
  }

  didBlurTicketTypeField(field: CustomField) {
    if (this.disabled) {
      return;
    }

    this.handleUpdate(field.id, this.fieldValues[field.id]);
  }

  /**
   * Simple handleUpdate method - it uses the disabled flag to
   * determine whether updates should be emitted
   */
  private handleUpdate(fieldId: string, value: any) {
    if (this.disabled) {
      return;
    }

    // Create new fields object
    const newFields = {
      ...this.fieldValues,
      [fieldId]: value,
    };

    // Update internal state
    this.fieldValues = newFields;

    // Update UI
    this.cdr.detectChanges();

    // Push to our RxJS stream with source info
    this.fieldUpdates.next({
      fieldId,
      value,
      source: this.updateSource,
    });
  }

  onInputChange(field: CustomField, event: any) {
    if (this.disabled) {
      return;
    }

    // Extract the value from the DOM event
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    this.handleUpdate(field.id, value);
  }

  getTicketTypeFieldLabel(field: CustomField, valueId: string | null | undefined): string {
    if (valueId === null || valueId === undefined || valueId === '') return 'None';

    if ('externalSelect' in field && typeof field.externalSelect === 'string' && field.externalSelect) {
      return (
        this.ticketTypeFieldLabels[CUSTOM_FIELD_TYPE_SINGLE_SELECT]?.[`${field.externalSelect}:${valueId}`] ??
        String(valueId)
      );
    }

    if (field.options && Array.isArray(field.options)) {
      const option = field.options.find((opt) => {
        if (opt === null || opt === undefined) {
          return false;
        }
        // Check if opt is an object and has an 'id' property before accessing it
        if (typeof opt === 'object' && Object.prototype.hasOwnProperty.call(opt, 'id')) {
          return String((opt as { id: any }).id) === valueId;
        }
        // Otherwise, treat opt as a primitive and compare its string representation
        return String(opt) === valueId;
      });

      // After find, option can be undefined if no match, or it can be the matched element.
      if (option !== undefined && option !== null) {
        // If the found option is an object and has a 'label' property
        if (typeof option === 'object' && Object.prototype.hasOwnProperty.call(option, 'label')) {
          return String((option as { label: any }).label);
        }
        // If it's an object and has an 'id' (but no label), or if it's a primitive
        if (typeof option === 'object' && Object.prototype.hasOwnProperty.call(option, 'id')) {
          return String((option as { id: any }).id);
        }
        // Fallback for primitives or objects without a known property to display
        return String(option);
      }
    }

    if (field.type === CUSTOM_FIELD_TYPE_USER_SELECT || field.type === CUSTOM_FIELD_TYPE_MULTI_USER_SELECT) {
      return this.ticketTypeFieldLabels?.[CUSTOM_FIELD_TYPE_USER_SELECT]?.[valueId] ?? String(valueId);
    }
    if (
      field.type === CUSTOM_FIELD_TYPE_USER_GROUP_SELECT ||
      field.type === CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT
    ) {
      return this.ticketTypeFieldLabels?.[CUSTOM_FIELD_TYPE_USER_GROUP_SELECT]?.[valueId] ?? String(valueId);
    }

    return String(valueId);
  }

  ticketTypeFieldsTrackBy(field: CustomField) {
    return field.id;
  }

  handleFileUpload(event: any, field: CustomField) {
    if (this.disabled || !event.target.files || event.target.files.length === 0) {
      return;
    }

    const file: File = event.target.files[0];

    // Optional: Check file size (example: 5MB)
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      console.error(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      // TODO: Show user-friendly error message (e.g., toast)
      return;
    }

    this.uploadingFieldId = field.id;
    this.cdr.detectChanges(); // Update UI to show spinner

    this.uploadService
      .uploadFilesForSlackConversation([file], this.conversationId)
      .pipe(takeUntil(this.destroy$)) // Ensure cleanup on component destroy
      .subscribe({
        next: (createdFiles) => {
          if (createdFiles && createdFiles.length > 0) {
            const newFileId = createdFiles[0].id;
            const currentValues = this.fieldValues[field.id];
            // Ensure currentValues is treated as an array, defaulting to empty if not or if wrong type
            const updatedArray = Array.isArray(currentValues)
              ? (currentValues.filter((item) => typeof item === 'string') as string[])
              : [];
            const updatedValues = [...updatedArray, newFileId];
            this.handleUpdate(field.id, updatedValues);
          }
          this.uploadingFieldId = null;
          this.cdr.detectChanges(); // Update UI
        },
        error: (err) => {
          console.error('File upload failed:', err);
          // TODO: Show user-friendly error message
          this.uploadingFieldId = null;
          this.cdr.detectChanges(); // Update UI
        },
      });

    // Reset file input to allow uploading the same file again if needed
    event.target.value = null;
  }

  getVisibleTicketTypeFields() {
    return getVisibleFields(this.fieldDefinitions, this.fieldValues);
  }

  private async approveAIPrefill(fieldId: string): Promise<void> {
    if (!this.conversationId) {
      console.error('Missing conversationId for approving prefill.');
      return;
    }
    try {
      await this.aiAutofillService.approvePrefill({
        conversationId: this.conversationId,
        fieldId,
      });

      // Update the metadata in the array
      const metadata = this.getFieldMetadata(fieldId);
      if (metadata) {
        metadata.approvedPrefill = true;
      }

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error approving prefill for field:', fieldId, error);
    }
  }

  private async rejectAIPrefill(fieldId: string): Promise<void> {
    if (!this.conversationId) {
      console.error('Missing conversationId for rejecting prefill.');
      return;
    }
    try {
      await this.aiAutofillService.rejectPrefill({
        conversationId: this.conversationId,
        fieldId,
      });

      // Update the metadata in the array
      const metadata = this.getFieldMetadata(fieldId);
      if (metadata) {
        metadata.approvedPrefill = false;
      }

      if (!this.fieldValues) {
        this.fieldValues = {};
      }
      this.fieldValues[fieldId] = null;
      this.fieldValuesChange.emit({ ...this.fieldValues });
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error rejecting prefill for field:', fieldId, error);
    }
  }

  async showPrefillOption(field: CustomField) {
    const LABEL_APPROVE_PREFILL = 'Approve Prefill';
    const LABEL_REJECT_PREFILL = 'Reject Prefill';

    const options: ActionDropdownType[] = [
      {
        label: LABEL_APPROVE_PREFILL,
        icon: 'thumbs-up-outline',
      },
      {
        label: LABEL_REJECT_PREFILL,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_APPROVE_PREFILL) {
            await this.approveAIPrefill(field.id);
          } else if (label === LABEL_REJECT_PREFILL) {
            await this.rejectAIPrefill(field.id);
          }
        },
      },
    });
    popover.present();
  }
}
